import React, { useState, useEffect } from "react";
import AdminSidebar from "./AdminSidebar";
import AdminTopBar from "./AdminTopBar";
import { useNavigate } from "react-router-dom";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";
import { useSearch } from "./contexts/SearchContext";
export default function AdminFaskes() {
  const { searchQuery } = useSearch();
  const [currentPageFaskes, setCurrentPageFaskes] = useState(1);
  const [currentPageDoctors, setCurrentPageDoctors] = useState(1);
  const [faskesList, setFaskesList] = useState([]);
  const [doctorList, setDoctorList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const itemsPerPage = 3;
  const navigate = useNavigate();
  const { logAdminActivity } = useActivityLogger();
  // Fetch data when component mounts
  useEffect(() => {
    logAdminActivity(
      "view",
      "view_admin_faskes_list",
      "faskes",
      null,
      "admin",
      "success",
      "",
      "Admin melihat daftar fasilitas kesehatan dan dokter"
    );
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      // Fetch Faskes data
      const faskesResponse = await api.get("/faskes");
      const faskesData =
        faskesResponse.data.results || faskesResponse.data || [];

      // Transform faskes data to match component structure
      const transformedFaskes = faskesData.map((faskes, index) => ({
        id: faskes.ID || index + 1,
        name: faskes.name || faskes.Nama || "Nama tidak tersedia",
        address: faskes.vicinity || faskes.Alamat || "Alamat tidak tersedia",
        phone:
          faskes.formatted_phone_number ||
          faskes.NomorTelepon ||
          "Nomor tidak tersedia",
        place_id: faskes.place_id || faskes.PlaceID,
      }));
      setFaskesList(transformedFaskes);

      // Fetch Dokter data
      const dokterResponse = await api.get("/dokter");
      const dokterData = dokterResponse.data || [];

      // Transform dokter data to match component structure
      const transformedDokter = dokterData.map((dokter, index) => ({
        id: dokter.ID || index + 1,
        name: dokter.nama || "Nama tidak tersedia",
        practice:
          dokter.faskes && dokter.faskes.length > 0
            ? dokter.faskes.map((f) => f.name || f.Nama).join(", ")
            : "Tempat praktik tidak tersedia",
        schedule:
          dokter.jadwal_praktik && dokter.jadwal_praktik.length > 0
            ? dokter.jadwal_praktik
                .map(
                  (jadwal) =>
                    `${jadwal.hari}, ${jadwal.jam_mulai?.slice(0, 5) || ""} - ${jadwal.jam_selesai?.slice(0, 5) || ""}`
                )
                .join("\n")
            : "Jadwal tidak tersedia",
      }));
      setDoctorList(transformedDokter);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Gagal memuat data. Silakan refresh halaman.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFaskes = async (id, name) => {
    const confirmed = window.confirm(`Yakin ingin menghapus faskes '${name}'?`);
    if (!confirmed) return;

    try {
      // Find the faskes to get place_id
      const faskes = faskesList.find((f) => f.id === id);
      if (!faskes || !faskes.place_id) {
        alert("Data faskes tidak valid untuk dihapus.");
        return;
      }

      await api.delete(`/faskes/${faskes.place_id}`);
      alert(`Faskes '${name}' berhasil dihapus.`);

      // Refresh data after deletion
      fetchData();
    } catch (err) {
      console.error("Error deleting faskes:", err);
      alert("Gagal menghapus faskes.");
    }
  };

  const handleDeleteDokter = async (id, name) => {
    const confirmed = window.confirm(`Yakin ingin menghapus dokter '${name}'?`);
    if (!confirmed) return;

    try {
      await api.delete(`/dokter/${id}`);
      alert(`Dokter '${name}' berhasil dihapus.`);

      // Refresh data after deletion
      fetchData();
    } catch (err) {
      console.error("Error deleting dokter:", err);
      alert("Gagal menghapus dokter.");
    }
  };
  const filteredFaskes = faskesList.filter(faskes =>
    faskes.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faskes.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faskes.phone?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const filteredDoctors = doctorList.filter(doctor =>
    doctor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doctor.practice?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  // Faskes pagination
  const lastIndexFaskes = currentPageFaskes * itemsPerPage;
  const firstIndexFaskes = lastIndexFaskes - itemsPerPage;
  const currentFaskes = filteredFaskes.slice(firstIndexFaskes, lastIndexFaskes);
  const totalPagesFaskes = Math.ceil(filteredFaskes.length / itemsPerPage);

  // Doctors pagination
  const lastIndexDoctors = currentPageDoctors * itemsPerPage;
  const firstIndexDoctors = lastIndexDoctors - itemsPerPage;
  const currentDoctors = filteredDoctors.slice(firstIndexDoctors, lastIndexDoctors);
  const totalPagesDoctors = Math.ceil(filteredDoctors.length / itemsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen flex">
        <AdminSidebar />
        <div className="flex-1 p-6 bg-gray-50">
          <AdminTopBar />
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Memuat data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <AdminSidebar />
      <div className="flex-1 p-6 bg-gray-50">
        <AdminTopBar />

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button
              onClick={fetchData}
              className="ml-4 text-sm underline hover:no-underline"
            >
              Coba Lagi
            </button>
          </div>
        )}

        {/* Faskes Section */}
        <div className="bg-white p-4 rounded shadow mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              Fasilitas Kesehatan ({faskesList.length})
            </h2>
            <div className="flex gap-2">
              {/* <button
                onClick={fetchData}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                disabled={loading}
              >
                🔄 Refresh
              </button> */}
              <button
                onClick={() => navigate("/admin/faskes/add")}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                + Tambah Faskes
              </button>
            </div>
          </div>

          {faskesList.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Tidak ada data faskes tersedia
            </div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr>
                    <th className="py-2 text-left">Nama Faskes</th>
                    <th className="py-2 text-left">Alamat</th>
                    <th className="py-2 text-left">No. HP</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {currentFaskes.map((item) => (
                    <tr key={item.id} className="border-b">
                      <td className="py-4">{item.name}</td>
                      <td className="py-4">{item.address}</td>
                      <td className="py-4">{item.phone}</td>
                      <td className="py-4 align-middle">
                        <div className="flex items-center gap-2">
                          <button
                            title="Edit Faskes"
                            onClick={() =>
                              navigate(`/admin/faskes/edit/${item.place_id}`)
                            }
                          >
                            <img
                              src="/icon-edit.png"
                              alt="Edit"
                              className="h-5 w-5"
                            />
                          </button>
                          <button
                            title="Hapus Faskes"
                            onClick={() =>
                              handleDeleteFaskes(item.id, item.name)
                            }
                          >
                            <img
                              src="/icon-trash.png"
                              alt="Delete"
                              className="h-5 w-5"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Faskes Pagination */}
              {totalPagesFaskes > 1 && (
                <div className="flex justify-end mt-4 gap-1">
                  <button
                    onClick={() =>
                      setCurrentPageFaskes((prev) => Math.max(prev - 1, 1))
                    }
                    className="border px-2 py-1 rounded"
                    disabled={currentPageFaskes === 1}
                  >
                    &lt;
                  </button>
                  {[...Array(totalPagesFaskes)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPageFaskes(i + 1)}
                      className={`border px-2 py-1 rounded ${
                        currentPageFaskes === i + 1
                          ? "bg-blue-500 text-white"
                          : ""
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() =>
                      setCurrentPageFaskes((prev) =>
                        Math.min(prev + 1, totalPagesFaskes)
                      )
                    }
                    className="border px-2 py-1 rounded"
                    disabled={currentPageFaskes === totalPagesFaskes}
                  >
                    &gt;
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Doctors Section */}
        <div className="bg-white p-4 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              Dokter ({doctorList.length})
            </h2>
            <button
              onClick={() => navigate("/admin/faskes/add-doctor")}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              + Tambah Dokter
            </button>
          </div>

          {doctorList.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Tidak ada data dokter tersedia
            </div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr>
                    <th className="py-2 text-left">Nama Dokter</th>
                    <th className="py-2 text-left">Tempat Praktik</th>
                    <th className="py-2 text-left">Jadwal Praktik</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {currentDoctors.map((doc) => (
                    <tr key={doc.id} className="border-b">
                      <td className="py-2">{doc.name}</td>
                      <td className="py-2">{doc.practice}</td>
                      <td className="py-2 whitespace-pre-line">
                        {doc.schedule}
                      </td>
                      <td className="py-2 align-middle">
                        <div className="flex items-center gap-2">
                          <button
                            title="Edit Dokter"
                            onClick={() =>
                              navigate(`/admin/faskes/edit-doctor/${doc.id}`)
                            }
                          >
                            <img
                              src="/icon-edit.png"
                              alt="Edit"
                              className="h-5 w-5"
                            />
                          </button>
                          <button
                            title="Hapus Dokter"
                            onClick={() => handleDeleteDokter(doc.id, doc.name)}
                          >
                            <img
                              src="/icon-trash.png"
                              alt="Delete"
                              className="h-5 w-5"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Doctors Pagination */}
              {totalPagesDoctors > 1 && (
                <div className="flex justify-end mt-4 gap-1">
                  <button
                    onClick={() =>
                      setCurrentPageDoctors((prev) => Math.max(prev - 1, 1))
                    }
                    className="border px-2 py-1 rounded"
                    disabled={currentPageDoctors === 1}
                  >
                    &lt;
                  </button>
                  {[...Array(totalPagesDoctors)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPageDoctors(i + 1)}
                      className={`border px-2 py-1 rounded ${
                        currentPageDoctors === i + 1
                          ? "bg-blue-500 text-white"
                          : ""
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() =>
                      setCurrentPageDoctors((prev) =>
                        Math.min(prev + 1, totalPagesDoctors)
                      )
                    }
                    className="border px-2 py-1 rounded"
                    disabled={currentPageDoctors === totalPagesDoctors}
                  >
                    &gt;
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
